import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import queryString from "query-string";
import { jwtDecode } from "jwt-decode";
import { routePaths } from "@/config";
import "./ImpersonateForm.styles.scss";
import { TMemberInfos } from "@/features/profile/types";
import { useAppDispatch } from "@/store";
import {
  getToken,
  getMemberInfos,
  updateProgress,
  updateMemberProfile,
} from "@/store/reducers/member/member.actions";
import { Toastify } from "@/utils/toast";
import { exchangeImpersonationTicketOnce } from "../../api/impersonationExchange";
import { TMemberProfile } from "../../types";

const DEFAULT_ERROR_MESSAGE =
  "Désolé, ce lien de connexion a expiré ou a déjà été utilisé.";

const ImpersonateForm: React.FC = () => {
  const [hasFailed, setHasFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const hasRunRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Ce composant peut être démonté puis remonté en plein vol (voir le
    // commentaire détaillé dans api/impersonationExchange.ts) : on passe donc
    // par un cache de promesse au niveau module. Un remontage se raccroche à
    // la MÊME promesse — le ticket, à usage unique, n'est jamais consommé
    // deux fois, et le composant n'est jamais bloqué sur le spinner faute de
    // promesse à laquelle s'abonner.
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const parsed = queryString.parse(window.location.search);
    const ticket = (parsed?.impersonation_ticket || "") as string;

    if (!ticket) {
      setHasFailed(true);
      return;
    }

    exchangeImpersonationTicketOnce(ticket)
      .then((response: any) => {
        const token = response.data.accessToken;
        const profile = response.data.profile as TMemberProfile;
        const decoded = jwtDecode(token);
        const sub = decoded.sub as any;

        const infosUser: TMemberInfos = {
          _id: sub?._id || "",
          email: sub?.email,
          role: sub?.role,
          username: sub?.username,
          memberId: sub?.memberId,
          isVerified: sub?.isVerified,
          isInEmailVerificationProcess: sub?.isInEmailVerificationProcess,
          impersonatedBy: sub?.impersonatedBy,
        };

        dispatch(getToken(token));
        dispatch(getMemberInfos({ ...infosUser }));
        dispatch(updateMemberProfile({ ...profile }));
        dispatch(updateProgress(0));

        navigate(routePaths.profile, { replace: true });
      })
      .catch((error) => {
        setHasFailed(true);

        const serverMessage = error?.response?.data?.message as
          | string
          | undefined;

        setErrorMessage(serverMessage || DEFAULT_ERROR_MESSAGE);

        if (serverMessage) {
          Toastify(serverMessage, "error", 5000);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="paiement-container">
      <div className="header-part">
        <h1 className="title">Connexion en tant que membre</h1>
      </div>

      <div className="loader-part-verif">
        <img
          src={require("@/assets/images/png/logo-mini.png")}
          className="logo-mini"
          alt="load-profil"
        />

        {!hasFailed ? (
          <>
            <img
              src={require("@/assets/images/gif/loadmore.gif")}
              className="load-profil"
              alt="load-profil"
            />
            <small>Connexion en cours...</small>
          </>
        ) : (
          <p>
            {errorMessage}
            <br />
            Veuillez retourner sur le back-office et réessayer.
          </p>
        )}
      </div>

      <div className="footer-part">
        <span
          className="link-primary"
          onClick={() => navigate(routePaths.authChildren.signInAbsolute)}
        >
          Retourner sur la page de connexion
        </span>
      </div>
    </div>
  );
};

export default ImpersonateForm;
